import { useState } from 'react';
import { Card, Button, Icon, PasswordInput } from '@atoms/index';
import { authService } from '@services/authService';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  showBackButton?: boolean;
}

export const ChangePasswordModal = ({ isOpen, onClose, onSuccess, showBackButton = true }: ChangePasswordModalProps) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!currentPassword.trim()) {
      errors.currentPassword = 'Mevcut şifre gereklidir';
    }

    if (!newPassword.trim()) {
      errors.newPassword = 'Yeni şifre gereklidir';
    } else if (newPassword.length < 8) {
      errors.newPassword = 'Yeni şifre en az 8 karakter olmalıdır';
    } else if (!/(?=.*[A-Z])/.test(newPassword)) {
      errors.newPassword = 'Yeni şifre en az 1 büyük harf içermelidir';
    } else if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(newPassword)) {
      errors.newPassword = 'Yeni şifre en az 1 özel karakter içermelidir';
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = 'Şifre onayı gereklidir';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Şifreler eşleşmiyor';
    }

    if (currentPassword === newPassword) {
      errors.newPassword = 'Yeni şifre mevcut şifre ile aynı olamaz';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      onSuccess();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Şifre değiştirilemedi';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
      <Card className="max-w-md w-full animate-scale-in">
        <div className="p-6">
          {showBackButton && (
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 -mt-1 transition-colors"
            >
              <Icon name="chevron-left" size={20} />
              <span>Profilime Dön</span>
            </button>
          )}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Şifre Değiştir</h3>
                <p className="text-sm text-gray-600 mt-1">İlk girişinizde şifrenizi değiştirmeniz zorunludur</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mevcut Şifre *</label>
              <PasswordInput
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  if (fieldErrors.currentPassword) setFieldErrors({ ...fieldErrors, currentPassword: '' });
                }}
                error={!!fieldErrors.currentPassword}
                disabled={isSubmitting}
                required
              />
              {fieldErrors.currentPassword && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.currentPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre *</label>
              <PasswordInput
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (fieldErrors.newPassword) setFieldErrors({ ...fieldErrors, newPassword: '' });
                }}
                error={!!fieldErrors.newPassword}
                disabled={isSubmitting}
                required
                helperText="En az 8 karakter, 1 büyük harf ve 1 özel karakter içermelidir"
              />
              {fieldErrors.newPassword && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.newPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre (Tekrar) *</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (fieldErrors.confirmPassword) setFieldErrors({ ...fieldErrors, confirmPassword: '' });
                }}
                error={!!fieldErrors.confirmPassword}
                disabled={isSubmitting}
                required
              />
              {fieldErrors.confirmPassword && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.confirmPassword}</p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                className="flex-1"
              >
                Şifreyi Değiştir
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};
